import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaTag, FaSearch, FaUser, FaCalendar, FaCog, FaRobot, FaBrain } from "react-icons/fa";

import Pagination from "Components/Pagination";
import BrandNameDetail from "Components/BrandNameDetail";
import CommonModal from "Components/CommonModal";
import { catchError, changeState } from "JS/common";
import { getBrandNameList } from "JS/API";
import { useMarkMenuSeen } from "Contexts/NewCountContext";

// 디자인 시스템 임포트
import PageContainer from "Components/UI/PageContainer";
import SearchCard from "Components/UI/SearchCard";
import FormField from "Components/UI/FormField";
import Select from "Components/UI/Select";
import Button from "Components/UI/Button";
import Badge from "Components/UI/Badge";

import { TableContainer, Table, TableHeader, TableCell } from "Components/UI/Table";
import { commonStyles, designTokens, listPageStyles } from "utils/designSystem";

const BrandNameList = () => {
  const navigate = useNavigate();
  useMarkMenuSeen("bandname");
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 10,
  });
  const [alert, setAlert] = useState(""); // 알림 메시지
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const [list, setList] = useState([]);
  const [modal, setModal] = useState(false);
  // 체크된 브랜드네이밍 항목 ID들을 저장하는 상태
  const [idArr, setIdArr] = useState([]);
  const [inquiryType, setInquiryType] = useState("");
  const [filterState, setFilterState] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [allChecked, setAllChecked] = useState(false);

  let prevent = false;

  // 추가 스타일
  const pageStyles = {
    searchSection: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: designTokens.spacing.lg,
      flexWrap: 'wrap'
    },
    filterGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
      flex: 1,
      minWidth: '300px'
    },
    controlGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs,
      fontWeight: '500'
    },
    userDetail: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    },
    methodInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs
    },
    categoryInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      fontSize: '13px'
    },
    dateInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs
    },
    clickableRow: {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    noDataContainer: listPageStyles.noDataContainer,
    checkboxCell: {
      width: '40px',
      padding: designTokens.spacing.xs
    },
    checkbox: {
      transform: 'scale(1.3)',
      cursor: 'pointer'
    },
    checkboxLabel: listPageStyles.checkboxLabel};

  // 생성 방법별 배지 variant
  const getMethodBadgeVariant = (method) => {
    const variants = {
      GPT: 'primary',
      LSTM: 'success'
    };
    return variants[method] || 'neutral';
  };

  // 생성 방법별 아이콘
  const getMethodIcon = (method) => {
    const icons = {
      GPT: <FaRobot size={12} />,
      LSTM: <FaBrain size={12} />
    };
    return icons[method] || <FaCog size={12} />;
  };

  //= 브랜드네이밍 항목 불러오기
  const getBrandName = async () => {
    // 브랜드네이밍 항목 불러오기
    if (prevent) return; // 중복 호출 방지
    prevent = true; // 중복 호출 방지
    setTimeout(() => {
      // 200ms 뒤에 실행
      prevent = false; // 중복 호출 방지 해제
    }, 200);

    const result = await getBrandNameList(
      pageInfo.page,
      pageInfo.limit,
      inquiryType
    ); // 브랜드네이밍 항목 불러오기
    if (typeof result === "object") {
      setList(result?.data?.data);
      changeState(setPageInfo, "totalPage", result?.data?.meta?.total_page);
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  // 개별 체크박스 변경 처리
  const handleIndividualCheck = (id, checked) => {
    let newIdArr;
    if (checked) {
      newIdArr = [...idArr, id];
    } else {
      newIdArr = idArr.filter(item => item !== id);
    }
    setIdArr(newIdArr);
    setAllChecked(newIdArr.length === list.length && list.length > 0);
  };

  // 전체 선택 처리
  const handleAllCheck = (checked) => {
    setAllChecked(checked);
    if (checked) {
      setIdArr(list.map(item => item.id));
    } else {
      setIdArr([]);
    }
  };

  // 필터 적용
  const handleFilterApply = () => {
    setPageInfo(prev => ({ ...prev, page: 1 }));
    setFilterState(!filterState);
  };

  // 필터 초기화
  const handleFilterReset = () => {
    setInquiryType("");
    setPageInfo(prev => ({ ...prev, page: 1 }));
  };

  //= 브랜드네이밍 항목 테이블 렌더
  const renderTableBody = () => {
    return list.map(
      ({
        id,
        user_id,
        method,
        input_text,
        generated_name,
        generated_meaning,
        created_at,
        input_maincode,
        input_subcode,
        user_name
      }) => {
        //& 목록 클릭 시 실행할 함수
        const onModal = () => {
          setModal(true);
          setSelectedItem({
            id,
            user_id,
            method,
            input_text,
            generated_name,
            generated_meaning,
            created_at,
          });
        };

        const checkDragText = (e) => {
          const selection = window.getSelection();
          if (selection.toString().length > 0) {
            return;
          }
          onModal(e);
        };

        const isChecked = idArr.includes(id);

        return (
          <tr key={id} style={pageStyles.clickableRow}>
            <TableCell style={pageStyles.checkboxCell}>
              <label style={pageStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  style={pageStyles.checkbox}
                  checked={isChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleIndividualCheck(id, e.target.checked);
                  }}
                />
              </label>
            </TableCell>

            <TableCell onClick={onModal} style={{ cursor: 'pointer', fontWeight: '600' }}>
              {id}
            </TableCell>

            <TableCell onClick={checkDragText}>
              <div style={pageStyles.userDetail}>
                <div style={pageStyles.userInfo}>
                  <FaUser size={12} style={{ color: designTokens.colors.text.tertiary }} />
                  {user_name}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: designTokens.colors.text.secondary,
                  marginLeft: '16px'
                }}>
                  ({user_id})
                </div>
              </div>
            </TableCell>

            <TableCell onClick={checkDragText}>
              <Badge 
                variant={getMethodBadgeVariant(method)}
                icon={getMethodIcon(method)}
              >
                {method}
              </Badge>
            </TableCell>

            <TableCell onClick={checkDragText}>
              <div style={pageStyles.categoryInfo}>
                <div style={{ fontWeight: '500' }}>
                  {input_maincode ? input_maincode + "류" : "-"}
                </div>
                <div style={{ color: designTokens.colors.text.secondary }}>
                  {input_subcode || "-"}
                </div>
              </div>
            </TableCell>

            <TableCell onClick={checkDragText}>
              <span style={{
                fontWeight: '500',
                wordBreak: 'break-all',
                lineHeight: '1.4'
              }}>
                {input_text}
              </span>
            </TableCell>

            <TableCell onClick={checkDragText}>
              <div style={pageStyles.dateInfo}>
                <FaCalendar size={12} style={{ color: designTokens.colors.text.tertiary }} />
                {created_at.split("T")[0]}
              </div>
            </TableCell>
          </tr>
        );
      }
    );
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 브랜드네이밍 항목 관리";
  }, []);

  // 의존성 배열 변경될 때마다 getBrandName함수를 다시 호출하도록 함
  useEffect(() => {
    getBrandName();
  }, [pageInfo.page, pageInfo.limit, filterState]);

  // 리스트 변경 시 체크 상태 초기화
  useEffect(() => {
    setIdArr([]);
    setAllChecked(false);
  }, [list]);

  return (
    <PageContainer
      title="BRANDNAME"
      icon={<FaTag />}
    >
      {/* 검색 및 필터 영역 */}
      <SearchCard>
        <div style={pageStyles.searchSection}>
          {/* 필터 그룹 */}
          <div style={pageStyles.filterGroup}>
            <FormField style={{ flex: 1, marginBottom: 0 }}>
              <Select
                value={inquiryType}
                onChange={(e) => setInquiryType(e.target.value)}
              >
                <option value="">모든 생성 유형</option>
                <option value="GPT">GPT</option>
                <option value="LSTM">LSTM</option>
              </Select>
            </FormField>

            <Button
              variant="primary"
              icon={<FaSearch />}
              onClick={handleFilterApply}
            >
              필터 적용
            </Button>

            {inquiryType && (
              <Button
                variant="secondary"
                onClick={handleFilterReset}
              >
                초기화
              </Button>
            )}
          </div>

          {/* 페이지 설정 */}
          <div style={pageStyles.controlGroup}>
            <Select
              value={pageInfo.limit}
              onChange={(e) => {
                setPageInfo((prev) => {
                  const clone = { ...prev };
                  clone.page = 1;
                  clone.limit = e.target.value;
                  return clone;
                });
              }}
              style={{ minWidth: '140px' }}
            >
              <option value={10}>10개씩 보기</option>
              <option value={30}>30개씩 보기</option>
              <option value={50}>50개씩 보기</option>
            </Select>
          </div>
        </div>
      </SearchCard>

      {/* 선택된 항목 정보 */}
      {idArr.length > 0 && (
        <div style={{
          ...commonStyles.card,
          backgroundColor: designTokens.colors.primary + '10',
          border: `1px solid ${designTokens.colors.primary}30`,
          marginBottom: designTokens.spacing.lg,
          padding: designTokens.spacing.md
        }}>
          <span style={{ fontWeight: '500', color: designTokens.colors.primary }}>
            {idArr.length}개 항목이 선택되었습니다.
          </span>
        </div>
      )}

      {/* 테이블 또는 빈 상태 */}
      {list?.length ? (
        <TableContainer>
          <Table>
            <colgroup>
              <col width="5%" />
              <col width="10%" />
              <col width="20%" />
              <col width="15%" />
              <col width="15%" />
              <col width="20%" />
              <col width="15%" />
            </colgroup>
            <thead>
              <tr>
                <TableHeader>
                  <label style={pageStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      style={pageStyles.checkbox}
                      checked={allChecked}
                      onChange={(e) => handleAllCheck(e.target.checked)}
                    />
                  </label>
                </TableHeader>
                <TableHeader>No.</TableHeader>
                <TableHeader>사용자</TableHeader>
                <TableHeader>생성방법</TableHeader>
                <TableHeader>상품 분류</TableHeader>
                <TableHeader>키워드</TableHeader>
                <TableHeader>생성일</TableHeader>
              </tr>
            </thead>
            <tbody>{renderTableBody()}</tbody>
          </Table>
        </TableContainer>
      ) : (
        <div style={pageStyles.noDataContainer}>
          <FaTag
            size={48}
            style={{
              color: designTokens.colors.text.muted,
              marginBottom: designTokens.spacing.md
            }}
          />
          <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: designTokens.spacing.xs }}>
            {inquiryType ? '해당 조건의 브랜드명이 없습니다' : '브랜드명이 없습니다'}
          </div>
          <div style={{ fontSize: '14px' }}>
            {inquiryType
              ? '다른 필터 조건으로 시도해보세요.'
              : '생성된 브랜드명이 표시될 예정입니다.'
            }
          </div>
        </div>
      )}

      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {/* 모달들 */}
      {modal && selectedItem && (
        <BrandNameDetail item={selectedItem} setModal={setModal} />
      )}
      
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
            else return;
          }}
        />
      )}
    </PageContainer>
  );
};

export default BrandNameList;